import Datepicker from 'components/ReusableComponents/Datepicker'
import Dropdown from 'components/ReusableComponents/ManageHotel/Dropdown'
import Toggle from 'components/ReusableComponents/Toggle'
import CustomCampaignEstimation from 'features/CustomCampaigns/EditCampaign/Estimation'
import { hasMeaningfulCutoffDate } from 'features/CustomCampaigns/campaignAudienceDefaults'
import { directfulServiceTypes } from 'main/directfulServices'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { formatNumberAsThousands } from 'utils/priceHelpers'

import styles from './EditCampaign.module.scss'

export const dripMediumOptions = {
  Text: 'Text',
  Email: 'Email',
  EmailAndText: 'EmailAndText',
  TextWithEmailFallback: 'TextWithEmailFallback'
}

export const dripMediumOptionsWithEmail = [
  dripMediumOptions.Email,
  dripMediumOptions.EmailAndText,
  dripMediumOptions.TextWithEmailFallback
]

export const dripMediumOptionsWithText = [
  dripMediumOptions.Text,
  dripMediumOptions.EmailAndText,
  dripMediumOptions.TextWithEmailFallback
]

export const dripMediumNames = {
  Text: 'Text Only',
  Email: 'Email Only',
  EmailAndText: 'Text + Email Together',
  TextWithEmailFallback: 'Text with Email Fallback'
}

function getGuestsToReach(audience, medium) {
  if (!audience) return 0

  if (medium === 'Email') {
    return {
      total: audience.emailSize || 0,
      toText: 0,
      toEmail: audience.emailSize || 0
    }
  }

  if (medium === 'Text') {
    return {
      total: audience.textSize || 0,
      toText: audience.textSize || 0,
      toEmail: 0
    }
  }

  if (medium === 'EmailAndText') {
    const n = audience.emailAndTextSize || 0
    return {
      total: n,
      toText: n,
      toEmail: n
    }
  }

  if (medium === 'TextWithEmailFallback') {
    return {
      total: audience.emailNotTextSize || 0,
      toText: audience.textSize || 0,
      toEmail: (audience.emailNotTextSize || 0) - (audience.textSize || 0)
    }
  }
}

const CampaignSetup = ({
  audienceId,
  setAudienceId,
  isEdibable,
  audiences,
  deliveryStartDate,
  setDeliveryStartDate,
  deliveryFinishDate,
  setDeliveryFinishDate,
  medium,
  setMedium,
  isAudienceCalculating
}) => {
  const {
    total: totalGuestsToReach,
    toText: guestsToText,
    toEmail: guestsToEmail
  } = getGuestsToReach(
    audiences && audiences.find(a => a.id === Number(audienceId)),
    medium
  )

  const activeServices = useSelector(s => s.hotelDetails.activeServices)

  const hasEmailService = activeServices.find(s => s.plan === directfulServiceTypes.Email)

  const [showCutOffDate, setShowCutOffDate] = useState(
    hasMeaningfulCutoffDate(deliveryFinishDate)
  )

  useEffect(() => {
    setShowCutOffDate(hasMeaningfulCutoffDate(deliveryFinishDate))
  }, [deliveryFinishDate])

  function handleChangeCutOffToggle(e) {
    const isChecked = e.target.checked

    if (isChecked) {
      setShowCutOffDate(true)
    } else {
      setShowCutOffDate(false)
      setDeliveryFinishDate(null)
    }
  }

  const mediumDescriptions = {
    Text: 'Sends messages only by text (SMS) to guests with a valid mobile number when a compliant customer–hotel relationship allows text communication.',
    Email:
      'Sends messages only by email to guests with a valid email address when a compliant customer–hotel relationship allows email communication.',
    TextWithEmailFallback:
      'Sends a text first to guests with a valid mobile number when a compliant customer–hotel relationship allows text communication. If the text cannot be sent, a separate email version of the message is sent instead when the relationship allows email communication.',
    EmailAndText:
      'Sends both a text and an email to guests at the same time—each with its own content—when a compliant customer–hotel relationship allows communication through both channels.'
  }

  return (
    <>
      <div className={styles.accordionContent}>
        {hasEmailService && (
          <>
            <Dropdown
              label="Message channel strategy"
              options={dripMediumNames}
              value={medium}
              onChange={e => setMedium(e.target.value)}
            />
            <p className={styles.audienceSize}>{mediumDescriptions[medium]}</p>
          </>
        )}
        <div className={styles.audienceDropdownRow}>
          <Dropdown
            label="Audience"
            value={audienceId}
            disabled={!isEdibable}
            options={audiences
              .filter(a => !a.isDeleted)
              .reduce((prev, curr) => ({ ...prev, [curr.id]: curr.name }), {})}
            onChange={e => setAudienceId(e.target.value)}
          />
          {isAudienceCalculating ? (
            <p className={styles.audienceSize}>Calculating audience size...</p>
          ) : (
            <p className={styles.audienceSize}>
              {audienceId && <>Approx. {formatNumberAsThousands(totalGuestsToReach)} </>}
              guests
            </p>
          )}
        </div>
        <div>
          <div className={styles.datepickerHolder}>
            <span className={styles.datepickerLabel}>Start sending texts on</span>
            <Datepicker
              disabled={!isEdibable}
              className={styles.datepicker}
              placeholderText="Select date"
              selected={deliveryStartDate ? moment(deliveryStartDate).toDate() : null}
              minDate={moment().toDate()}
              onChange={date =>
                setDeliveryStartDate(moment(date).startOf('day').format())
              }
            />
            {moment(deliveryStartDate).isBefore(moment().startOf('day')) && (
              <p className={styles.error}>Start date cannot be in the past</p>
            )}
          </div>
          <label className={styles.cutOffToggleHolder}>
            <Toggle handleChange={handleChangeCutOffToggle} checked={showCutOffDate} />
            <div className={styles.cutOffToggleLabel}>
              <p className={styles.cutOffToggleLabelTitle}>Add cut-off date</p>
              <p className={styles.cutOffToggleLabelDescription}>
                Enable this to stop sending texts at a certain date
              </p>
            </div>
          </label>
          {showCutOffDate && (
            <div className={styles.datepickerHolder}>
              <span className={styles.datepickerLabel}>Cut-off date</span>
              <Datepicker
                disabled={!isEdibable}
                className={styles.datepicker}
                placeholderText="Select date"
                selected={
                  hasMeaningfulCutoffDate(deliveryFinishDate)
                    ? moment(deliveryFinishDate).toDate()
                    : null
                }
                minDate={
                  deliveryStartDate
                    ? moment(deliveryStartDate).toDate()
                    : moment().toDate()
                }
                onChange={date =>
                  setDeliveryFinishDate(moment(date).endOf('day').format())
                }
              />
              {hasMeaningfulCutoffDate(deliveryFinishDate) &&
                moment(deliveryFinishDate).isBefore(deliveryStartDate) && (
                <p className={styles.error}>Cut-off date should be after start date</p>
              )}
            </div>
          )}
        </div>
      </div>
      {!isAudienceCalculating && (
        <CustomCampaignEstimation
          deliveryStartDate={deliveryStartDate}
          deliveryFinishDate={deliveryFinishDate}
          totalGuestsToReach={totalGuestsToReach}
          guestsToText={guestsToText}
          guestsToEmail={guestsToEmail}
        />
      )}
    </>
  )
}

export default CampaignSetup
